/**
 * Participant session — persisted to `localStorage` so a refresh / back-button
 * doesn't lose the interview context (architecture §5.1). Cleared on
 * navigation to /complete or /already-completed.
 *
 * Stored shape:
 *   leaply:interview:session = { email, studyId, interviewId }
 */

const KEY = "leaply:interview:session";

export interface InterviewSession {
  email: string;
  studyId: string;
  interviewId: string;
  /** Research-side UUID (users.id) — the Amplitude user_id for real
   *  participants. Absent for dev/test sessions (device_id only). */
  userId?: string;
  /** True for admin-QA sessions (entered via ?studyId only, email typed in).
   *  Stamped onto every Amplitude event as `is_test` so test runs can be
   *  filtered out of the real funnel. */
  isTest?: boolean;
}

export function loadSession(): InterviewSession | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<InterviewSession>;
    if (
      typeof parsed.email === "string" &&
      typeof parsed.studyId === "string" &&
      typeof parsed.interviewId === "string"
    ) {
      return {
        email: parsed.email,
        studyId: parsed.studyId,
        interviewId: parsed.interviewId,
        userId: typeof parsed.userId === "string" ? parsed.userId : undefined,
        isTest: parsed.isTest === true,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function saveSession(s: InterviewSession): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* QuotaExceeded etc. — best-effort */
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* no-op */
  }
}
